import React from "react";
import { Link } from "react-router-dom";
import { usePublicContentBySlug } from "../hooks/usePublicContentBySlug";
import { formatCmsBodyForDisplay } from "../utils/sanitizeHtml";
import Loader from "./Loader";

function formatUpdatedAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

/**
 * @param {{ slugCandidates: string[], pageLabel?: string, emptyMessage?: string }} props
 */
export default function CmsContentPage({
  slugCandidates,
  pageLabel,
  emptyMessage = "No content is available yet.",
}) {
  const { loading, error, content } = usePublicContentBySlug(slugCandidates);

  if (loading) {
    return <Loader label="Loading content…" />;
  }

  if (error) {
    return (
      <div className="cms-page">
        <div className="card card-error cms-card" role="alert">
          <h2 className="card-title">Something went wrong</h2>
          <p className="cms-card__muted">{error}</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="cms-page">
        <div className="card card-empty cms-card">
          <h2 className="card-title">Content not found</h2>
          <p className="cms-card__muted">{emptyMessage}</p>
          <Link to="/" className="cms-back-link">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  const safeHtml = formatCmsBodyForDisplay(content.body);
  const updated = formatUpdatedAt(content.updatedAt);

  return (
    <div className="cms-page">
      <nav className="cms-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span className="cms-breadcrumb__sep" aria-hidden>
          /
        </span>
        <span className="cms-breadcrumb__current">
          {pageLabel || content.title || "Page"}
        </span>
      </nav>

      <article className="cms-surface">
        <header className="cms-header">
          {pageLabel && (
            <p className="cms-header__eyebrow">{pageLabel}</p>
          )}
          <h1 className="cms-header__title">{content.title || "Untitled"}</h1>
          <div className="cms-header__meta">
            {content.slug && (
              <span className="cms-meta-pill" title="URL slug">
                {content.slug}
              </span>
            )}
            {updated && (
              <time className="cms-header__date" dateTime={content.updatedAt}>
                Updated {updated}
              </time>
            )}
          </div>
        </header>

        <div
          className="cms-prose"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </article>

      <div className="cms-footer-nav">
        <Link to="/" className="cms-back-link">
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
